import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { oapi, fmtWhen } from '@/lib/officialsApi';
import { OfficialAvatar } from './StatusPill';
import { useOverrideFlow } from './ConfirmWarningsDialog';

const Footer = ({ children }) => <div className="flex items-center justify-end gap-2 pt-2">{children}</div>;

export const BulkAssignDialog = ({ open, orgId, games, roles, officials, onClose, onDone }) => {
  const [role, setRole] = useState('');
  const [pick, setPick] = useState(null);
  const [q, setQ] = useState('');
  const [instructions, setInstructions] = useState('');
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);
  const { run, dialog } = useOverrideFlow();
  const allRoles = useMemo(() => {
    const m = new Map((roles || []).map(r => [r.key, r.label]));
    games.forEach(g => g.crew.forEach(c => m.set(c.role_key, c.label)));
    return [...m].map(([key, label]) => ({ key, label }));
  }, [roles, games]);
  useEffect(() => { if (open) { setPick(null); setQ(''); setInstructions(''); setRole(allRoles[0]?.key || ''); } }, [open, allRoles]);
  const list = (officials || []).filter(o => o.status !== 'inactive' && (!q || o.full_name.toLowerCase().includes(q.toLowerCase())));

  const submit = async () => {
    setBusy(true);
    try {
      const items = games.filter(g => g.game_status !== 'cancelled').map(g => ({ event_id: g.event_id, match_id: g.match_id, role_key: role, official_id: pick.id, instructions }));
      const res = await run((override) => oapi('/officials/assignments/bulk', { method: 'POST', orgId, body: { items, notify, override } }));
      if (res) { toast.success(`${pick.full_name} assigned to ${res.count} game(s)`); onDone(); }
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <>
      <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
        <DialogContent className="max-w-lg" data-testid="bulk-assign-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#00247D]">Assign to {games.length} game{games.length === 1 ? '' : 's'}</DialogTitle>
            <DialogDescription>Availability and conflicts are checked for every game before anything is saved.</DialogDescription>
          </DialogHeader>
          {games.some(g => g.is_past) && (
            <div className="text-xs rounded-md bg-sky-50 border border-sky-200 text-sky-900 px-3 py-2" data-testid="bulk-past-note">
              {games.filter(g => g.is_past).length} of these games {games.filter(g => g.is_past).length === 1 ? 'has' : 'have'} already been played. The official is recorded as <b>Accepted</b> on those games without an email.
            </div>
          )}
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger data-testid="bulk-role-select"><SelectValue placeholder="Role" /></SelectTrigger>
            <SelectContent>{allRoles.map(r => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
          </Select>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search officials…" className="pl-8" data-testid="bulk-search-input" />
          </div>
          <div className="max-h-56 overflow-y-auto space-y-0.5">
            {list.map(o => (
              <button key={o.id} type="button" onClick={() => setPick(o)} data-testid={`bulk-official-${o.id}`}
                className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-left text-sm ${pick?.id === o.id ? 'bg-[#00247D]/10 text-[#00247D] font-semibold' : 'hover:bg-slate-50'}`}>
                <OfficialAvatar name={o.full_name} photo={o.photo_path} size="w-7 h-7" />{o.full_name}
              </button>
            ))}
          </div>
          <Textarea rows={2} value={instructions} onChange={e => setInstructions(e.target.value)} placeholder="Special instructions (optional)" />
          <Footer>
            <label className="flex items-center gap-2 text-sm text-slate-600 mr-auto"><Switch checked={notify} onCheckedChange={setNotify} /> Email the official</label>
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button disabled={!pick || !role || busy} onClick={submit} className="bg-[#00247D] hover:bg-[#001A5C]" data-testid="bulk-assign-submit">
              {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}Assign
            </Button>
          </Footer>
        </DialogContent>
      </Dialog>
      {dialog}
    </>
  );
};

export const CopyCrewDialog = ({ open, orgId, source, games, preselected, onClose, onDone }) => {
  const [targets, setTargets] = useState(new Set());
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);
  const { run, dialog } = useOverrideFlow();
  const key = (g) => `${g.event_id}:${g.match_id}`;
  const options = games.filter(g => source && key(g) !== key(source) && g.game_status !== 'cancelled');
  useEffect(() => { if (open) setTargets(new Set((preselected || []).filter(k => source && k !== key(source)))); }, [open, preselected, source]);
  const toggle = (k) => setTargets(prev => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const submit = async () => {
    setBusy(true);
    try {
      const body = { source_event_id: source.event_id, source_match_id: source.match_id, notify,
        targets: [...targets].map(k => { const [event_id, match_id] = k.split(':'); return { event_id, match_id }; }) };
      const res = await run((override) => oapi('/officials/assignments/copy-crew', { method: 'POST', orgId, body: { ...body, override } }));
      if (res) { toast.success(`Crew copied · ${res.count} assignment(s) created`); onDone(); }
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const crew = (source?.assignments || []).filter(a => ['pending', 'accepted'].includes(a.status));
  return (
    <>
      <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
        <DialogContent className="max-w-lg" data-testid="copy-crew-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#00247D]">Copy crew</DialogTitle>
            <DialogDescription>{crew.map(a => `${a.official_name} (${a.role_label})`).join(', ') || 'No officials on this game yet'}</DialogDescription>
          </DialogHeader>
          <div className="max-h-72 overflow-y-auto divide-y border rounded-lg">
            {options.map(g => (
              <label key={key(g)} className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50">
                <Checkbox checked={targets.has(key(g))} onCheckedChange={() => toggle(key(g))} data-testid={`copy-target-${g.match_id}`} />
                <span className="flex-1 min-w-0 truncate">{g.team_a_name} vs {g.team_b_name}</span>
                <span className="text-xs text-slate-500 shrink-0">{fmtWhen(g)}</span>
              </label>
            ))}
            {!options.length && <p className="p-4 text-sm text-slate-500">No other games in this view.</p>}
          </div>
          <Footer>
            <label className="flex items-center gap-2 text-sm text-slate-600 mr-auto"><Switch checked={notify} onCheckedChange={setNotify} /> Email officials</label>
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button disabled={!targets.size || !crew.length || busy} onClick={submit} className="bg-[#00247D] hover:bg-[#001A5C]" data-testid="copy-crew-submit">
              {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}Copy to {targets.size} game{targets.size === 1 ? '' : 's'}
            </Button>
          </Footer>
        </DialogContent>
      </Dialog>
      {dialog}
    </>
  );
};

const METHODS = [['phone', 'Phone call'], ['whatsapp', 'WhatsApp'], ['text', 'Text message'], ['in_person', 'In person'], ['email', 'Email'], ['other', 'Other']];

export const ManualConfirmDialog = ({ open, orgId, assignment, onClose, onDone }) => {
  const [method, setMethod] = useState('phone');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setMethod('phone'); setNote(''); } }, [open]);
  const submit = async () => {
    setBusy(true);
    try {
      await oapi(`/officials/assignments/${assignment.id}/confirm`, { method: 'POST', orgId, body: { method, note } });
      toast.success('Marked as accepted (confirmed by admin)');
      onDone();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="manual-confirm-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#00247D]">Confirm for {assignment?.official_name}</DialogTitle>
          <DialogDescription>Record that the official accepted outside Schedule Guru. It's logged as confirmed by an admin.</DialogDescription>
        </DialogHeader>
        <Select value={method} onValueChange={setMethod}>
          <SelectTrigger data-testid="manual-confirm-method"><SelectValue /></SelectTrigger>
          <SelectContent>{METHODS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <Textarea rows={2} value={note} onChange={e => setNote(e.target.value)} placeholder="Note (optional)" data-testid="manual-confirm-note" />
        <Footer>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="manual-confirm-submit">Mark accepted</Button>
        </Footer>
      </DialogContent>
    </Dialog>
  );
};

const ACTION_LABEL = {
  assigned: 'Assigned', accepted: 'Accepted', declined: 'Declined', cancelled: 'Cancelled', confirmed_manually: 'Confirmed manually',
  game_changed: 'Game changed', reminder_sent: 'Reminder sent', notified: 'Email re-sent', instructions_updated: 'Instructions updated',
};

export const AuditDialog = ({ open, orgId, assignment, onClose }) => {
  const [items, setItems] = useState(null);
  useEffect(() => {
    if (!open || !assignment) return;
    setItems(null);
    oapi('/officials/audit', { orgId, query: { assignment_id: assignment.id } }).then(d => setItems(d.items)).catch(e => toast.error(e.message));
  }, [open, assignment, orgId]);
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="audit-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#00247D]">History · {assignment?.official_name}</DialogTitle>
          <DialogDescription>{assignment?.role_label}</DialogDescription>
        </DialogHeader>
        {!items ? <Loader2 className="w-5 h-5 animate-spin mx-auto my-6 text-slate-400" /> : (
          <ol className="max-h-80 overflow-y-auto space-y-2.5 border-l-2 border-slate-200 pl-4">
            {items.map(i => (
              <li key={i.id} className="text-sm">
                <div className="font-semibold text-slate-800">{ACTION_LABEL[i.action] || i.action}
                  <span className="font-normal text-slate-500"> · {i.actor_type === 'official' ? 'by the official' : i.actor_type === 'system' ? 'automatic' : `by ${i.actor_name || 'admin'}`}</span>
                </div>
                <div className="text-xs text-slate-500">{new Date(i.at).toLocaleString()}</div>
                {i.details?.reason && <div className="text-xs text-slate-600">Reason: {i.details.reason}</div>}
                {i.details?.method && <div className="text-xs text-slate-600">Via {i.details.method}{i.details.note ? ` · ${i.details.note}` : ''}</div>}
                {i.details?.overridden_warnings?.length > 0 && <div className="text-xs text-orange-700">Warnings overridden: {i.details.overridden_warnings.join(', ')}</div>}
                {i.details?.to && <div className="text-xs text-slate-600">Now {i.details.to.date} {i.details.to.time} · {i.details.to.venue}</div>}
              </li>
            ))}
          </ol>
        )}
      </DialogContent>
    </Dialog>
  );
};
